import { withinContactHours } from './contactHours.js';

// THE CALL AND TEXT BUTTONS ON THE LEAD SCREEN (Leads centre polish, B1). Pure.
//
// A button is SHOWN when the club has the service (Speed to Lead calls for Call, AI SMS for Text) and is DISABLED WITH A REASON rather than
// hidden when something stops it right now. ⚠️ A DEMO lead is NOT a reason: its buttons are enabled and open the dialog or composer, and the
// screen itself says "This is a demo lead" on submit (the server's demo guard is unchanged and still refuses). So client demos can show the
// feature. ⚠️ This is a DISPLAY rule only: every guard is checked again, server-side, at the action.

export type ContactDisabledCode = 'no_permission' | 'kill_switch' | 'do_not_contact' | 'opted_out' | 'outside_hours';

export interface ContactAction {
  enabled: boolean;
  /** Why it is disabled (absent when enabled). A code the screen can test, never prose a log would carry. */
  code: ContactDisabledCode | null;
  /** The short reason the screen prints beside the disabled button. */
  message: string | null;
}

export interface ContactStateFacts {
  /** The club has Speed to Lead calls (its own switch is on). */
  hasCalls: boolean;
  /** The club has AI SMS. */
  hasText: boolean;
  /** The viewer holds `kyckconnect_contact` (any Kyck staff user does). */
  canContact: boolean;
  /** The system-wide calls switch is on. */
  callsSwitchOn: boolean;
  doNotContact: boolean;
  optedOut: boolean;
  now: Date;
  clubTimezone: string | null;
}

export const CONTACT_REASON_TEXT: Record<ContactDisabledCode, { call: string; text: string }> = {
  no_permission: { call: 'Ask Kyck for access to call and text leads', text: 'Ask Kyck for access to call and text leads' },
  kill_switch: { call: 'Calls are switched off for now', text: 'Calls are switched off for now' },
  do_not_contact: { call: 'Marked do not contact', text: 'Marked do not contact' },
  opted_out: { call: 'Opted out of texts', text: 'Opted out of texts' },
  outside_hours: { call: 'Calls from 7am', text: 'Texts from 7am' },
};

const off = (code: ContactDisabledCode, kind: 'call' | 'text'): ContactAction => ({ enabled: false, code, message: CONTACT_REASON_TEXT[code][kind] });
const ON: ContactAction = { enabled: true, code: null, message: null };

/** The Call button's state, or null when the club has no calls (the button is not shown at all). First reason wins, in this order. */
export function callActionOf(f: ContactStateFacts): ContactAction | null {
  if (!f.hasCalls) return null;
  if (!f.canContact) return off('no_permission', 'call');
  if (!f.callsSwitchOn) return off('kill_switch', 'call');
  if (f.doNotContact) return off('do_not_contact', 'call');
  if (!withinContactHours(f.now, f.clubTimezone)) return off('outside_hours', 'call');
  return ON;
}

/** The Text button's state, or null when the club has no AI SMS (the button and composer are not shown at all). */
export function textActionOf(f: ContactStateFacts): ContactAction | null {
  if (!f.hasText) return null;
  if (!f.canContact) return off('no_permission', 'text');
  if (f.doNotContact) return off('do_not_contact', 'text');
  if (f.optedOut) return off('opted_out', 'text');
  if (!withinContactHours(f.now, f.clubTimezone)) return off('outside_hours', 'text');
  return ON;
}

/** What the screen says when a person submits on a DEMO lead. The screen answers; nothing reaches the server and nothing is dialled or sent. */
export const DEMO_CALL_MESSAGE = 'This is a demo lead, so nothing was dialled.';
export const DEMO_TEXT_MESSAGE = 'This is a demo lead, so nothing was sent.';
